/**
 * GymSelector — full-page gym selector shown at "/".
 * Publicly accessible: visitors browse gyms without login.
 * Login is triggered when clicking a gym card or the header "Sign in" button.
 * Optional Google Maps panel shows gym locations on a map (<GymSelectorMap>).
 *
 * Sign-in runs on the shared `useAuth` hook with `gymId: null` — the same
 * bootstrap the in-gym app uses, minus its gym-scoped half. What stays here is
 * what only this page does: restoring the saved language and theme from the
 * account, and opening/closing the login modal around the shared <LoginCard>.
 */
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router";
import { db } from "../firebase";
import { collection, doc, getDoc, getDocs, onSnapshot, query, where, limit, getCountFromServer } from "firebase/firestore";
import { F, DEFAULT_ACCENT } from "../constants";
import { haversineDistance, formatDistance, isGymOpen, getNextOpenTime, initials, avatarPhotoURL, getNavigationUrl, getSubdomainRedirect, todayStr, currentHref, chunkForInQuery, formatDateSpan } from "../utils";
import { useAppTheme } from "../contexts/ThemeContext";
import { LangSelect, IconLock, SELECTABLE, ModalBackdrop, SearchInput, EmptyState, StatusBarTintBar } from "./ui";
import { statusBarInset } from "../utils/iosStatusBar";
import { localStore } from "../utils/safeStorage";
import ProfileMenu from "./ProfileMenu";
import { markSeen, getRegisteredTokenId } from "../utils/pushNotifications";
import { isPushDiscoveryPending } from "../utils/notifPrefs";
import { peekPendingDestination, takePendingDestination, clearPendingDestination } from "../utils/pendingDestination";
import { useTranslation } from "../i18n";
import { useAuth } from "../hooks/useAuth";
import { useCurrentUser } from "../contexts/CurrentUserContext";
import { useListenerRetry } from "../hooks/useListenerRetry";
import { getPlatformConfig } from "../utils/platformConfig";
import { useGeolocation } from "../hooks/useGeolocation";
import { WeekSchedule } from "./WeekSchedule";
import { LoadingBlank } from "./LoadingSkeleton";
import { LoginCard } from "./LoginCard";
import { GradeRangeSummary } from "./gym/GradeRangeSummary";
import { GymSelectorMap } from "./gym/GymSelectorMap";
import { logEvent, EVT } from "../analytics";
import { usePullToRefresh, PullToRefreshIndicator } from "../hooks/usePullToRefresh";
import { useInstallBanner } from "../hooks/useInstallBanner";
import { InstallBanner } from "./InstallBanner";
import { version as appVersion } from "../buildInfo";
import { compareNames, resolvePlatformCaps } from "@sendly/shared";

// When a gym has more than one comp in `status === "active"` (e.g. a long
// event plus an upcoming one, or an admin who left a finished comp open),
// the gym card should surface the comp that is "now" — currently running or
// soonest upcoming — not whichever one Firestore returned first.
//
// Logic: split active comps into "still relevant" (endDate missing OR
// endDate ≥ today) and "fully past" (endDate < today). If there is at least
// one still-relevant comp, return the one with the earliest startDate — so a
// currently running comp wins over an upcoming one, and the soonest upcoming
// wins over later ones. If there are ONLY past comps (admin forgot to close
// the previous event but nothing new is scheduled), fall back to the most
// recently ended one rather than hiding everything — the past comp is still
// the latest thing happening at the gym, so the badge stays informative.
/**
 * The recently-visited gym ids from localStorage, shape-checked.
 *
 * Same rule as the `sendly-gym-index` cache below: the key outlives the
 * build that wrote it, so a non-array (or a truncated write) must come back
 * as an empty list, not reach `.includes` / `.indexOf` in the sort memo and
 * white-screen the front door with no way past but clearing site data.
 */
function readRecentGymIds() {
  const parsed = localStore.getJSON("sendly-recent-gyms", []);
  return Array.isArray(parsed) ? parsed.filter(id => typeof id === "string") : [];
}

function pickUpcomingActiveComp(comps, timezone) {
  if (!Array.isArray(comps) || comps.length === 0) return null;
  // Gym-tz "today" so a comp's badge doesn't flip a few hours off around
  // midnight (comp start/end are gym-calendar dates). Falls back to browser-local.
  const today = todayStr(timezone);
  const relevant = [];
  const past = [];
  for (const c of comps) {
    if (!c) continue;
    const end = c.endDate || c.startDate;
    if (!end || end >= today) relevant.push(c);
    else past.push(c);
  }
  if (relevant.length > 0) {
    relevant.sort((a, b) => (a.startDate || "").localeCompare(b.startDate || ""));
    return relevant[0];
  }
  past.sort((a, b) => (b.endDate || b.startDate || "").localeCompare(a.endDate || a.startDate || ""));
  return past[0] || null;
}

export function GymSelector() {
  const { t, lang, setLang } = useTranslation();
  const navigate = useNavigate();
  const { pullY, refreshing, PULL_THRESHOLD } = usePullToRefresh();
  // The same login bootstrap the in-gym app runs, with no gym in context: it
  // owns the auth listener, the users/{uid} create-or-login write and every
  // sign-in handler the login card needs. `gymId: null` short-circuits the
  // member-doc / gymIds half — see the hook's header comment.
  const {
    authUser, authLoading, seedName,
    emailInput, setEmailInput, passwordInput, setPasswordInput,
    emailSent, setEmailSent, authError, setAuthError,
    needsEmailConfirm, handleConfirmEmail,
    isStandalone, isIOSStandalone, isIOS, isPasswordAuthEnabled,
    handleLogin, handleAppleLogin, handleEmailLink, handleLogout,
    handlePasswordSignIn, handlePasswordSignUp,
  } = useAuth({ gymId: null, lang });
  // The user doc arrives from the app-wide provider (index.js); this page
  // used to keep its own listener on it.
  const { userData } = useCurrentUser();
  // Initialize public gyms from localStorage cache for instant rendering.
  // The shape check is not paranoia: this key survives deploys, so a value
  // written by an older build (or a truncated write) outlives the code that
  // wrote it. A non-array here used to reach a spread and throw "gyms is not
  // iterable" — a white front door with no way past it but clearing site data.
  const [publicGyms, setPublicGyms] = useState(() => {
    const parsed = localStore.getJSON("sendly-gym-index");
    return Array.isArray(parsed) ? parsed : [];
  });
  // Non-public gyms that the current signed-in user can see:
  // - platformAdmins see every active+non-public gym;
  // - everyone else sees any active+non-public gym they're a member of
  //   (i.e. have entered at least once), regardless of role.
  // Loaded by a separate effect once `authUser` + `userData` are both known.
  // These are merged into the main `gyms` list with a `_nonPublic: true`
  // marker that the card renderer uses to dim + badge them.
  const [extraGyms, setExtraGyms] = useState([]);
  // Merged list — always read `gyms` (not publicGyms) for display.
  const gyms = useMemo(() => {
    if (extraGyms.length === 0) return publicGyms;
    const publicIds = new Set(publicGyms.map((g) => g.id));
    const uniqueExtras = extraGyms.filter((g) => !publicIds.has(g.id));
    return uniqueExtras.length === 0 ? publicGyms : [...publicGyms, ...uniqueExtras];
  }, [publicGyms, extraGyms]);
  const [showAll, setShowAll] = useState(false);
  const [search, setSearch] = useState("");
  const { latitude, longitude, loading: geoLoading, requestLocation } = useGeolocation({ autoRequest: true });
  // Header name. The live user doc is authoritative; `seedName` is the hook's
  // uid-keyed cache, which is what keeps the first paint from flashing the
  // email prefix (users with no Google displayName) or the Google name (users
  // who set a custom one) while Firestore resolves. Each gym enter/leave is a
  // full page load, so without the cache that flash happens every time.
  const userName = userData?.name || seedName || null;
  const [expandedGym, setExpandedGym] = useState(null);
  const [expandedGymData, setExpandedGymData] = useState(null);
  // Friendships (global — not gym-scoped)
  const [friendships, setFriendships] = useState([]);
  const { setUserTheme, th, accent } = useAppTheme();

  // Public GymSelector state
  const [showLogin, setShowLogin] = useState(false);
  const [pendingGymSlug, setPendingGymSlug] = useState(null);
  // True while the login modal is open because an in-app destination is
  // waiting (see pendingDestination.js) — only drives the modal's hint line.
  const [pendingDest, setPendingDest] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [platformConfig, setPlatformConfig] = useState(null);

  // Legacy `?section=recap` unsubscribe links (sent before settings moved to
  // the profile page) → the email settings section.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("section") !== "recap") return;
      navigate("/profile#settings/emails", { replace: true });
    } catch {}
  }, [navigate]);

  // Subdomain shortcut: gi-boulder.app.sendly.hu → app.sendly.hu/gym/gi-boulder
  // Custom domain: app.giboulder.hu → /gym/gi-boulder (same origin, useMainApp handles cross-origin redirect)
  useEffect(() => {
    const sub = getSubdomainRedirect();
    if (sub) {
      window.location.replace(`${window.location.protocol}//${sub.baseDomain}/gym/${sub.slug}`);
      return;
    }
    // Custom domain: resolve hostname → gym slug, redirect same-origin
    const h = window.location.hostname;
    if (!h || h === "localhost" || /^[\d.:]+$/.test(h)
        || h.endsWith(".web.app") || h.endsWith(".firebaseapp.com")) return;
    // Fast path: cached slug from previous visit
    const cached = localStore.get("sendly-custom-domain-slug");
    if (cached) { window.location.replace("/gym/" + cached); return; }
    // Slow path: Firestore lookup
    getDocs(query(collection(db, "gyms"), where("customDomain", "==", h)))
      .then(snap => {
        if (!snap.empty) {
          const slug = snap.docs[0].data().slug;
          localStore.set("sendly-custom-domain-slug", slug);
          window.location.replace("/gym/" + slug);
        }
      });
  }, []);

  // PWA: set static manifest (gym pages set their own dynamic manifest)
  useEffect(() => {
    const old = document.querySelector('link[rel="manifest"]');
    if (old) old.remove();
    const link = document.createElement("link");
    link.rel = "manifest";
    link.href = "/manifest.json";
    document.head.appendChild(link);
  }, []);

  // The install invitation for the PLATFORM app — the manifest pinned just
  // above is the one in play here, so a tap installs the whole of Sendly
  // rather than any one gym. No feature flag to consult: gymPwa decides
  // whether a GYM may have its own app, and this page has no gym.
  const {
    installPrompt, showInstallBanner, handleInstallPwa, dismissInstallBanner,
  } = useInstallBanner({ isStandalone });

// Load platform config (for map settings)
  useEffect(() => {
    getPlatformConfig().then(setPlatformConfig).catch(() => {});
  }, []);

  // Mark notifications seen when the gym selector is opened / returns to focus —
  // mirrors the in-gym effect (App.js) so the cross-device "clear" also fires
  // when the user checks the app at the gym-list level, not only inside a gym.
  // allowWithoutToken lets a non-push device trigger the clear; gated on having
  // received a push before so it doesn't fire for users who never use push.
  // pushMeta is read via a ref (NOT an effect dep) — see App.js for why: a dep
  // would auto-fire markSeen on push ARRIVAL, prematurely advancing lastSeenAt.
  // The mount call waits for the user doc and is replayed when it lands, also
  // mirroring App.js: without that, a freshly loaded page never calls at all.
  const userDataRef = useRef(userData);
  userDataRef.current = userData;
  const runMarkSeenRef = useRef(null);
  const userDataLoaded = !!userData;
  useEffect(() => {
    if (!authUser?.uid) return;
    let last = 0;
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      // No doc yet and no own push token → the gate cannot be evaluated and
      // the call would no-op; skip without burning the throttle slot.
      if (!userDataRef.current && !getRegisteredTokenId()) return;
      const now = Date.now();
      if (now - last < 30000) return;
      last = now;
      markSeen({ allowWithoutToken: !!userDataRef.current?.pushMeta?.lastNotifiedAt });
    };
    runMarkSeenRef.current = onVisible;
    onVisible();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      runMarkSeenRef.current = null;
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [authUser?.uid]);

  useEffect(() => {
    if (userDataLoaded) runMarkSeenRef.current?.();
  }, [userDataLoaded]);

  // A sign-in closes the login modal; an auth failure opens it. The hook
  // reports failures through `authError` and asks for the address again
  // through `needsEmailConfirm` — on the selector the login card only exists
  // inside this modal, so without these two an email-link error would land
  // nowhere the visitor can see it.
  useEffect(() => {
    if (authUser) { setShowLogin(false); return; }
    if (authError || needsEmailConfirm) setShowLogin(true);
  }, [authUser, authError, needsEmailConfirm]);

  // Restore the saved theme once per mount. Mirrors `useMainApp`s
  // themeSyncedRef one-shot so a change made later in this session is not
  // overwritten. The language is NOT restored here: the shared sign-in
  // bootstrap (`useAuth`) restores it on every surface, a gym page included.
  const prefsRestoredRef = useRef(false);
  useEffect(() => {
    if (prefsRestoredRef.current || !userData) return;
    prefsRestoredRef.current = true;
    if (userData.theme) setUserTheme(userData.theme);
  }, [userData, setUserTheme]);

  // The uid-keyed name cache that seeds the header on the next cold load is
  // written by `CurrentUserProvider`, straight off the live doc. This page kept
  // its own copy of that write, guessing at the avatar preference whenever the
  // doc had not resolved yet; there is nothing left to guess.

  // Prefetch last-visited gym data in background to warm Firestore offline cache.
  // When the user navigates to that gym, onSnapshot fires instantly from cache.
  // Skipped in gym PWA mode (user goes straight to gym, no selector).
  const prefetchedRef = useRef(null);
  useEffect(() => {
    if (!userData?.lastGymId) return;
    const gymId = userData.lastGymId;
    // Only prefetch once per gym per session
    if (prefetchedRef.current === gymId) return;
    // Skip if we're in a standalone PWA (gym PWA goes directly to gym)
    const isStandalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
    if (isStandalone) return;
    prefetchedRef.current = gymId;
    // Fire-and-forget: populate offline cache with gym doc + subcollections.
    // `ascents` and `ratings` dropped (gym-activity-member-scope, 2026-09-24):
    // both are member-only reads now, and membership isn't checked before
    // this fires — a non-member would just draw a silent `permission-denied`
    // here. The gym app reads both in windowed slices anyway once inside, so
    // a whole-collection prefetch was never buying much.
    const prefetch = () => {
      getDoc(doc(db, "gyms", gymId)).catch(() => {});
      const cols = ["routes", "members"];
      cols.forEach(c => getDocs(collection(db, "gyms", gymId, c)).catch(() => {}));
    };
    if ("requestIdleCallback" in window) requestIdleCallback(prefetch);
    else setTimeout(prefetch, 1000);
  }, [userData?.lastGymId]);

  // Pending destination / gym slug redirect — runs whenever authUser becomes
  // non-null. Two flavours of "the visitor was already going somewhere when we
  // asked them to sign in":
  //   - a whole in-app path, stashed by the /profile guard (the recap mail's
  //     unsubscribe link deep-links `#settings/emails`, see pendingDestination.js)
  //   - a gym slug + optional tab hash, stashed when a gym card was tapped
  // The path wins: it is the more specific of the two and is the one this
  // session actually arrived on.
  useEffect(() => {
    if (!authUser) return;
    const pendingPath = takePendingDestination();
    if (pendingPath) {
      localStore.remove("sendly-pending-gym-slug");
      localStore.remove("sendly-pending-gym-tab");
      window.location.href = pendingPath;
      return;
    }
    const pendingSlug = localStore.get("sendly-pending-gym-slug");
    if (pendingSlug) {
      const pendingTab = localStore.get("sendly-pending-gym-tab") || "";
      localStore.remove("sendly-pending-gym-slug");
      localStore.remove("sendly-pending-gym-tab");
      window.location.href = "/gym/" + pendingSlug + pendingTab;
    }
  }, [authUser]);

  // Dismissing the modal is an answer too: "not now". Drop the waiting
  // destination with it, otherwise it sits in localStorage and re-opens this
  // modal on the next visit to the gym list — and teleports them off it the
  // moment they sign in for some unrelated reason.
  const closeLogin = useCallback(() => {
    setShowLogin(false);
    setEmailSent(false);
    setPendingDest(false);
    // Clearing the error is what makes dismissing stick: the effect above
    // reopens the modal while an error stands, and a leftover error would also
    // greet the visitor the next time they open the card.
    setAuthError("");
    clearPendingDestination();
  }, [setEmailSent, setAuthError]);

  // A destination is waiting and nobody is signed in — the visitor followed a
  // link into the app (today: the unsubscribe row of a recap or admin-report
  // mail) and was bounced here. Ask them to sign in right away instead of
  // leaving them on the gym list wondering what happened to the page they
  // tapped. Auth has to have settled first, otherwise the modal flashes open
  // for the split second before a signed-in session is restored.
  useEffect(() => {
    if (authLoading || authUser) return;
    if (!peekPendingDestination()) return;
    setPendingDest(true);
    setShowLogin(true);
  }, [authLoading, authUser]);

  // Load gym index (lightweight: 1 Firestore read instead of N)
  // Falls back to direct gym loading if gymIndex doesn't exist yet
  const fallbackLoadedRef = useRef(false);
  useEffect(() => {
    const unsub = onSnapshot(doc(db, "platform", "gymIndex"), (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        const list = data.gyms || [];
        setPublicGyms(list);
        localStore.setJSON("sendly-gym-index", list);
      } else if (!fallbackLoadedRef.current) {
        // gymIndex not yet created — fall back to direct gym collection loading
        fallbackLoadedRef.current = true;
        const q2 = query(collection(db, "gyms"), where("isActive", "==", true));
        getDocs(q2).then(async (qSnap) => {
          const list = [];
          const compPromises = [];
          qSnap.docs.forEach(d => {
            const dd = d.data();
            if (dd.isPublic === false) return;
            const entry = {
              id: d.id, slug: dd.slug || d.id, name: dd.name || "", city: dd.city || "",
              location: dd.location || null, accentColor: dd.accentColor || DEFAULT_ACCENT,
              openingHours: dd.openingHours || null, timezone: dd.timezone || null, specialHours: dd.specialHours || null,
              stats: dd.stats || {}, hasLogo: !!dd.logoUrl, hasCover: !!dd.coverImageUrl,
              logoUrl: dd.logoUrl || null, coverImageUrl: dd.coverImageUrl || null,
              contactPhone: dd.contactPhone || "", contactEmail: dd.contactEmail || "",
              address: dd.address || "", socialLinks: dd.socialLinks || null,
              activeComp: null,
            };
            list.push(entry);
            if (dd.features?.competitions) {
              compPromises.push(
                getDocs(query(collection(db, "gyms", d.id, "competitions"), where("status", "==", "active"), limit(3)))
                  .then(compSnap => {
                    if (compSnap.empty) return;
                    const cd = pickUpcomingActiveComp(compSnap.docs.map(s => s.data()), dd.timezone);
                    if (cd) entry.activeComp = { name: cd.name || "", startDate: cd.startDate || null, endDate: cd.endDate || null };
                  }).catch(() => {})
              );
            }
          });
          await Promise.all(compPromises);
          setPublicGyms(list);
        }).catch(e => console.error("Gym fallback load failed:", e));
      }
    }, (error) => {
      // Firestore listener error (IndexedDB failure, permission denied, etc.)
      console.error("Gym index listener error:", error);
      // Fall back to direct gym collection query
      if (!fallbackLoadedRef.current) {
        fallbackLoadedRef.current = true;
        getDocs(query(collection(db, "gyms"), where("isActive", "==", true)))
          .then(qSnap => {
            const list = qSnap.docs
              .map(d => ({ id: d.id, ...d.data() }))
              .filter(g => g.isPublic !== false)
              .map(g => ({
                id: g.id, slug: g.slug || g.id, name: g.name || "", city: g.city || "",
                location: g.location || null, accentColor: g.accentColor || DEFAULT_ACCENT,
                stats: g.stats || {}, logoUrl: g.logoUrl || null, coverImageUrl: g.coverImageUrl || null,
                activeComp: null,
              }));
            setPublicGyms(list);
          }).catch(e => console.error("Gym direct load also failed:", e));
      }
    });
    return unsub;
  }, []);

  // Load non-public gyms that the signed-in user can see.
  // Only runs once authUser + userData are both loaded (anonymous visitors
  // never see non-public gyms regardless of what's in their localStorage).
  // - platformAdmin users: query all active gyms, keep only the non-public
  //   ones (simpler than a composite query — platformAdmin is a rare role
  //   so the extra reads are absorbed).
  // - everyone else: iterate their userData.gymIds, check each membership
  //   doc exists and isn't banned, load the gym doc to confirm it's still
  //   active and genuinely non-public. Any role qualifies — a member doc
  //   means the user has entered the gym, so it belongs in their selector.
  // Each returned entry gets `_nonPublic: true` so the card renderer can
  // dim it + add a badge.
  useEffect(() => {
    if (!authUser?.uid || !userData) {
      setExtraGyms([]);
      return;
    }
    const isPlatAdmin = userData.platformRole === "platformAdmin"
      || (userData.platformCaps || []).includes("platform.gym.adminAny");
    const memberGymIds = userData.gymIds || [];
    let cancelled = false;

    const mapGymDocToEntry = (id, d) => ({
      id,
      slug: d.slug || id,
      name: d.name || "",
      city: d.city || "",
      location: d.location || null,
      accentColor: d.accentColor || DEFAULT_ACCENT,
      openingHours: d.openingHours || null,
      timezone: d.timezone || null,
      specialHours: d.specialHours || null,
      // Start with zeros — the async block below overwrites these with live
      // `.count()` results. The gym-doc `stats` field is a stale snapshot and
      // nothing updates it on route create/archive, so we can't trust it.
      stats: { activeRoutes: 0, activeRoutesBoulder: 0, activeRoutesLead: 0, totalUsers: 0 },
      hasLogo: !!d.logoUrl,
      hasCover: !!d.coverImageUrl,
      logoUrl: d.logoUrl || null,
      coverImageUrl: d.coverImageUrl || null,
      contactPhone: d.contactPhone || "",
      contactEmail: d.contactEmail || "",
      address: d.address || "",
      socialLinks: d.socialLinks || null,
      disciplines: d.disciplines?.length ? d.disciplines : ["boulder"],
      activeComp: null,
      _nonPublic: true,
    });

    // Compute live route counts for a non-public gym entry via `.count()`
    // aggregate queries. Mirrors the Cloud Function's `rebuildGymIndex` logic
    // so non-public gyms show the same per-discipline split as public ones.
    // Legacy routes without a `discipline` field are attributed to boulder.
    // Active competition routes are subtracted so the public-facing total
    // matches what climbers see on the map (where active comp routes are
    // hidden until they enter a running session).
    const fetchLiveCounts = async (entry) => {
      const routesCol = collection(db, `gyms/${entry.id}/routes`);
      const compsCol = collection(db, `gyms/${entry.id}/competitions`);
      const [totalSnap, bSnap, lSnap, activeCompsSnap] = await Promise.all([
        getCountFromServer(query(routesCol, where("active", "==", true))).catch(() => null),
        getCountFromServer(query(routesCol, where("active", "==", true), where("discipline", "==", "boulder"))).catch(() => null),
        getCountFromServer(query(routesCol, where("active", "==", true), where("discipline", "==", "lead"))).catch(() => null),
        getDocs(query(compsCol, where("status", "==", "active"))).catch(() => null),
      ]);
      if (!totalSnap) return;
      let totalActive = totalSnap.data().count;
      let bCount = bSnap?.data().count || 0;
      let lCount = lSnap?.data().count || 0;

      // Subtract active-comp routes from the totals.
      const compRouteIds = new Set();
      activeCompsSnap?.docs.forEach((d) => {
        const ids = d.data()?.settings?.routeIds || [];
        ids.forEach((id) => compRouteIds.add(id));
      });
      if (compRouteIds.size > 0) {
        const docPromises = chunkForInQuery([...compRouteIds]).map(batch =>
          getDocs(query(routesCol, where("__name__", "in", batch))).catch(() => null)
        );
        const chunks = await Promise.all(docPromises);
        let compActiveTotal = 0, compActiveBoulder = 0, compActiveLead = 0;
        chunks.forEach((s) => {
          s?.docs.forEach((rd) => {
            const r = rd.data();
            if (r.active !== true) return;
            compActiveTotal++;
            if (r.discipline === "lead") compActiveLead++;
            else compActiveBoulder++; // legacy/missing → boulder
          });
        });
        totalActive = Math.max(0, totalActive - compActiveTotal);
        bCount = Math.max(0, bCount - compActiveBoulder);
        lCount = Math.max(0, lCount - compActiveLead);
      }

      const legacy = Math.max(0, totalActive - bCount - lCount);
      entry.stats.activeRoutes = totalActive;
      entry.stats.activeRoutesBoulder = bCount + legacy;
      entry.stats.activeRoutesLead = lCount;

      // Surface the most relevant active comp on the card (matches public
      // gym index behavior: skip stale finished-but-still-active comps).
      const cd = pickUpcomingActiveComp(activeCompsSnap?.docs.map(s => s.data()) || [], entry.timezone);
      if (cd) entry.activeComp = { name: cd.name || "", startDate: cd.startDate || null, endDate: cd.endDate || null };
    };

    (async () => {
      const found = [];
      try {
        if (isPlatAdmin) {
          // Single-field query on `isPublic == false` — reads at most the
          // handful of non-public gyms, not every active gym on the platform.
          // The single-field index is automatic; no firestore.indexes.json
          // entry needed.
          const qNonPublic = query(collection(db, "gyms"), where("isPublic", "==", false));
          const snap = await getDocs(qNonPublic);
          snap.docs.forEach((d) => {
            const dd = d.data();
            if (dd.isActive === false) return; // still require active
            found.push(mapGymDocToEntry(d.id, dd));
          });
        } else if (memberGymIds.length > 0) {
          // Fetch member + gym docs for every gym the user is a member of,
          // in parallel. Each entry is (2 reads); filtering down to
          // non-public gyms happens after the fetches complete.
          const results = await Promise.all(
            memberGymIds.map(async (gymId) => {
              try {
                const [memberSnap, gymSnap] = await Promise.all([
                  getDoc(doc(db, `gyms/${gymId}/members/${authUser.uid}`)),
                  getDoc(doc(db, `gyms/${gymId}`)),
                ]);
                if (!memberSnap.exists() || !gymSnap.exists()) return null;
                const md = memberSnap.data();
                // Any member (not just gymAdmin) sees their own non-public
                // gyms — a member doc means they've entered at least once.
                // Banned members are excluded (the ban CF also strips the
                // gymId from userData.gymIds, so this is belt-and-braces).
                if (md.banned === true) return null;
                const gd = gymSnap.data();
                if (gd.isActive === false) return null;
                if (gd.isPublic !== false) return null; // already in public index
                return mapGymDocToEntry(gymId, gd);
              } catch {
                return null;
              }
            }),
          );
          results.forEach((r) => { if (r) found.push(r); });
        }
        // Overlay live route counts (activeRoutes + per-discipline split) on
        // every non-public entry. Runs in parallel; errors leave the zero
        // init in place rather than failing the whole batch.
        await Promise.all(found.map((e) => fetchLiveCounts(e).catch(() => {})));
      } catch (e) {
        console.warn("Admin-only gym load failed:", e);
      }
      if (!cancelled) setExtraGyms(found);
    })();

    return () => { cancelled = true; };
  }, [authUser?.uid, userData?.platformRole, (userData?.platformCaps || []).join(","), (userData?.gymIds || []).join(",")]);

  // Load friendships (global, not gym-scoped). The gym selector only needs the
  // incoming-request count for the profile-button badge — the friend list, the
  // add/accept/remove flows and the friends' profiles all live on /profile.
  // The error callback is the shared retry's (a listener without one dies
  // silently, and a session ending under it is not an error — useListenerRetry).
  const { nonce: friendshipsNonce, onError: onFriendshipsError, markAlive: friendshipsAlive } =
    useListenerRetry("gymSelector", authUser?.uid || "");
  useEffect(() => {
    if (!authUser) { setFriendships([]); return; }
    const q2 = query(collection(db, "friendships"), where("users", "array-contains", authUser.uid));
    const applySnap = (data) => setFriendships(data);
    const unsub = onSnapshot(q2, snap => {
      friendshipsAlive();
      applySnap(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, onFriendshipsError("friendships"));
    // Browsers throttle/suspend background connections (and iOS suspends the
    // PWA long-poll), so a change made in a parallel session (other browser /
    // PWA) can fail to stream in until this tab is refocused. Re-fetch on
    // visibility regain to reconcile. (Restores the behaviour from commit
    // 17cee03 that was lost in a later refactor.)
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        getDocs(q2).then(snap => applySnap(snap.docs.map(d => ({ id: d.id, ...d.data() })))).catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => { unsub(); document.removeEventListener("visibilitychange", handleVisibility); setFriendships([]); };
  }, [authUser, friendshipsNonce, onFriendshipsError, friendshipsAlive]);

  const pendingIncoming = useMemo(() =>
    friendships.filter(f => f.status === "pending" && f.requestedBy !== authUser?.uid),
    [friendships, authUser]
  );

  // Handle gym card click — auth-aware, saves to recent gyms.
  // Optional `tabHash` (e.g. "#comp") makes the user land directly on a
  // specific tab inside the gym — `useMainApp.getTabFromHash` reads the
  // hash on mount and selects the matching tab.
  // Optional `searchParams` (e.g. "?calFocus=list") is inserted between
  // the path and the hash so deep links can pass extra signals (e.g. the
  // competition chip forcing list view on the Comp tab).
  const handleGymClick = useCallback((gym, tabHash, searchParams) => {
    const slug = gym.slug || gym.id;
    // Track recent gyms in localStorage for instant sort on next visit
    let prevGymId = "none";
    const recent = readRecentGymIds();
    if (recent.length > 0) prevGymId = recent[0];
    const updated = [gym.id, ...recent.filter(id => id !== gym.id)].slice(0, 5);
    localStore.setJSON("sendly-recent-gyms", updated);
    logEvent(EVT.GYM_SWITCH, { from_gym_id: prevGymId, to_gym_id: gym.id });
    const path = "/gym/" + slug + (searchParams || "") + (tabHash || "");
    if (authUser) {
      window.location.href = path;
    } else {
      localStore.set("sendly-pending-gym-slug", slug);
      if (tabHash) {
        // Stash the desired tab hash so the post-login redirect lands
        // straight on it. Read + cleared by the gym page on mount.
        localStore.set("sendly-pending-gym-tab", tabHash);
      }
      setPendingGymSlug(slug);
      setShowLogin(true);
    }
  }, [authUser]);

  // Filter, sort (recent first), and calculate distances
  const INITIAL_LIMIT = 10;
  const recentGymIds = useMemo(() => readRecentGymIds(), [gyms]); // re-read when gyms change (covers page load)

  const sortedGyms = useMemo(() => {
    let list = [...gyms];

    // Filter by search
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(g =>
        (g.name || "").toLowerCase().includes(q) ||
        (g.city || "").toLowerCase().includes(q)
      );
    }

    // Calculate distances if location available
    if (latitude && longitude) {
      list = list.map(g => {
        const loc = g.location;
        const dist = loc ? haversineDistance(latitude, longitude, loc.latitude || loc._lat, loc.longitude || loc._long) : null;
        return { ...g, _dist: dist };
      });
    }

    // Split into recent and rest
    const recent = [];
    const rest = [];
    list.forEach(g => {
      if (recentGymIds.includes(g.id)) recent.push(g);
      else rest.push(g);
    });

    // Sort recent by recency order
    recent.sort((a, b) => recentGymIds.indexOf(a.id) - recentGymIds.indexOf(b.id));

    // Sort rest by distance (if available) or name
    if (latitude && longitude) {
      rest.sort((a, b) => {
        if (a._dist == null && b._dist == null) return 0;
        if (a._dist == null) return 1;
        if (b._dist == null) return -1;
        return a._dist - b._dist;
      });
    } else {
      rest.sort((a, b) => compareNames(a.name, b.name, "hu"));
    }

    return [...recent, ...rest];
  }, [gyms, search, latitude, longitude, recentGymIds]);

  // Visible gyms: show limited list until user requests all
  const visibleGyms = useMemo(() => {
    if (showAll || search) return sortedGyms;
    return sortedGyms.slice(0, INITIAL_LIMIT);
  }, [sortedGyms, showAll, search]);
  const hasMore = sortedGyms.length > INITIAL_LIMIT && !showAll && !search;

  // Platform accent color (loaded from platform/config, fallback to default green)

  // Whether map feature is available
  const mapEnabled = platformConfig?.showGymSelectorMap && !!platformConfig?.googleMapsApiKey;

  // Enter a gym from a map marker InfoWindow. Same auth-aware behaviour as a
  // gym card tap, minus the recent-gyms bookkeeping the card does.
  const handleMapEnter = useCallback((slug) => {
    if (authUser) {
      window.location.href = "/gym/" + slug;
    } else {
      localStore.set("sendly-pending-gym-slug", slug);
      setPendingGymSlug(slug);
      setShowLogin(true);
    }
  }, [authUser]);

  if (authLoading) return <LoadingBlank />;


  return (
    <div style={{ minHeight: "100vh", background: th.bg, fontFamily: F }}>
      {/* Pull-to-refresh indicator */}
      <PullToRefreshIndicator pullY={pullY} refreshing={refreshing} PULL_THRESHOLD={PULL_THRESHOLD} />
      {/* Install the platform app — a pinned card in the shared top strip. */}
      <InstallBanner
        show={showInstallBanner}
        installPrompt={installPrompt}
        isIOS={isIOS}
        onInstall={handleInstallPwa}
        onDismiss={dismissInstallBanner}
      />
      {/* The selector has no header block — its top strip is the page itself. */}
      <StatusBarTintBar color={th.bg} />
      {/* Header */}
      <div style={{ padding: "16px 20px", paddingTop: 16 + statusBarInset(), display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: "1px solid " + th.border }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <svg width="28" height="28" viewBox="0 0 128 128" fill="none">
            <rect width="128" height="128" rx="28" fill={accent}/>
            <path d="M88 18 C88 18 42 18 42 44 C42 62 88 62 88 80 C88 106 42 106 42 106" stroke="white" strokeWidth="7" strokeLinecap="round" fill="none"/>
            <circle cx="76" cy="22" r="8" fill="#eab308"/>
            <circle cx="50" cy="36" r="8" fill="#dc2626"/>
            <circle cx="74" cy="58" r="8" fill="rgba(255,255,255,0.85)"/>
            <circle cx="84" cy="78" r="8" fill="#3d8b37"/>
            <circle cx="54" cy="98" r="8" fill="#eab308"/>
          </svg>
          <span style={{ fontSize: 18, fontWeight: 900, color: th.text }}>Sendly</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {!authUser && (
            <LangSelect value={lang} onChange={setLang} fontFamily={F} compact />
          )}
          {authUser ? (
            <>
              <ProfileMenu
                font={F}
                avatarUrl={avatarPhotoURL(authUser, userData)}
                initials={initials(userName || authUser.displayName || authUser.email)}
                name={userName || authUser.displayName || authUser.email?.split("@")[0]}
                showPaBadge={userData?.platformRole === "platformAdmin"}
                notifCount={pendingIncoming.length + (isPushDiscoveryPending(userData) ? 1 : 0)}
                items={[
                  // One entry for the whole profile — stats AND settings live
                  // there as tabs now.
                  { key: "profile", icon: "👤", label: t('profile.menuMyProfile'), badge: true, onClick: () => navigate("/profile", { state: { from: currentHref() } }) },
                  ...(resolvePlatformCaps(userData).size > 0 ? [{
                    key: "admin", icon: "🛡️", label: t('gymSelector.menuPlatformAdmin'), href: "/admin",
                    // Ctrl/Cmd/Shift+click → let the browser open /admin in a new tab.
                    // Plain click stays in-app via the router (no full reload).
                    onClick: (e) => { if (e.metaKey || e.ctrlKey || e.shiftKey) return; e.preventDefault(); navigate("/admin"); },
                  }] : []),
                  { key: "help", icon: "📖", label: t('gymSelector.menuHelp'), href: "https://sendly.rocks/docs/intro", target: "_blank", rel: "noopener noreferrer" },
                  { key: "feedback", icon: "💬", label: t('gymSelector.menuFeedback'), href: `mailto:support@sendly.rocks?subject=${encodeURIComponent(t('gymSelector.feedbackSubject', { version: appVersion }))}&body=${encodeURIComponent(t('gymSelector.feedbackBody', { version: appVersion }))}` },
                  { key: "logout", icon: "🚪", label: t('gymSelector.menuLogout'), muted: true, onClick: () => handleLogout() },
                ]}
              />
            </>
          ) : (
            <button onClick={() => { setPendingGymSlug(null); setShowLogin(true); }} style={{
              padding: "8px 20px", borderRadius: 10, border: "2px solid " + accent,
              background: "transparent", color: accent, fontSize: 13, fontWeight: 700,
              cursor: "pointer", fontFamily: F, whiteSpace: "nowrap",
            }}>
              {t('gymSelector.signIn')}
            </button>
          )}
        </div>
      </div>

      {/* Search + nearby + map toggle */}
      <div style={{ maxWidth: 800, margin: "0 auto", padding: "20px 16px 0" }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: th.text, marginBottom: 16 }}>{t('gymSelector.title')}</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
          <SearchInput
            value={search} onChange={setSearch}
            placeholder={t('gymSelector.search')}
            style={{ flex: "1 1 220px", minWidth: 0 }}
          />
          <div style={{ display: "flex", gap: 8, flex: "0 0 auto" }}>
            <button onClick={requestLocation} disabled={geoLoading} style={{
              height: 44, padding: "0 16px", borderRadius: 12, border: "1px solid " + th.border, background: latitude ? accent : th.card, color: latitude ? "#fff" : th.text,
              fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: F, display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="10" r="3"/><path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 0 0-16 0c0 3 2.7 7 8 11.7z"/></svg>
              {geoLoading ? "..." : t('gymSelector.nearbyBtn')}
            </button>
            {mapEnabled && (
              <button onClick={() => setShowMap(v => !v)} style={{
                height: 44, padding: "0 16px", borderRadius: 12, border: "1px solid " + th.border,
                background: showMap ? accent : th.card, color: showMap ? "#fff" : th.text,
                fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: F,
                display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg>
                {t('gymSelector.mapToggle')}
              </button>
            )}
            <button onClick={() => navigate("/calendar")} style={{
              height: 44, padding: "0 16px", borderRadius: 12, border: "1px solid " + accent,
              background: th.card, color: accent,
              fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: F,
              display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
            }} aria-label={t('gymSelector.calendarBtn')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              {t('gymSelector.calendarBtn')}
            </button>
          </div>
        </div>

        {/* Google Maps panel */}
        {showMap && mapEnabled && (
          <GymSelectorMap
            apiKey={platformConfig.googleMapsApiKey}
            gyms={sortedGyms}
            latitude={latitude}
            longitude={longitude}
            onEnter={handleMapEnter}
          />
        )}

        {/* Gym grid */}
        {sortedGyms.length === 0 ? (
          <EmptyState size="lg">
            <div style={{ fontSize: 40, marginBottom: 12 }}>🏔️</div>
            <div style={{ fontSize: 15 }}>{search ? t('gymSelector.noResults') : t('gymSelector.noGyms')}</div>
          </EmptyState>
        ) : (
          <>
            {/* Hover state styles for the gym cards + the inline competition
                block. Per-card accent is exposed via the `--ga` CSS variable
                set inline on each card so the hover tints can blend with it. */}
            <style>{`
              .sendly-gym-card {
                transition: box-shadow 0.15s ease, border-color 0.15s ease;
              }
              @media (hover: hover) {
                /* Lift the card on hover via shadow + an accent ring only —
                   no transform, so the content (and its text) stays put.
                   The ring makes the frame read as slightly larger/raised
                   without nudging anything around. */
                .sendly-gym-card:hover {
                  box-shadow: 0 0 0 2px var(--ga, ${accent}), 0 8px 24px rgba(0, 0, 0, 0.12);
                  border-color: var(--ga, ${accent});
                }
                .sendly-gym-card-comp {
                  transition: background 0.12s ease, border-color 0.12s ease;
                }
                .sendly-gym-card-comp:hover {
                  background: color-mix(in srgb, var(--ga, ${accent}) 18%, transparent) !important;
                  border-color: color-mix(in srgb, var(--ga, ${accent}) 55%, transparent) !important;
                }
                /* Arrow reveals via opacity only — it already reserves its
                   layout slot, so fading it in never nudges the comp text. */
                .sendly-gym-card-comp .sendly-gym-card-comp-arrow {
                  opacity: 0;
                  transition: opacity 0.12s ease;
                }
                .sendly-gym-card-comp:hover .sendly-gym-card-comp-arrow {
                  opacity: 1;
                }
              }
            `}</style>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, paddingBottom: 40 }}>
            {visibleGyms.map(gym => {
              const openStatus = isGymOpen(gym.openingHours, gym.timezone, gym.specialHours);
              const nextOpen = openStatus === false ? getNextOpenTime(gym.openingHours, gym.timezone, gym.specialHours) : null;
              const isExpanded = expandedGym === gym.id;
              const hasSpecialHours = Array.isArray(gym.specialHours) && gym.specialHours.length > 0;
              const hasDetails = gym.contactPhone || gym.contactEmail || gym.socialLinks?.facebook || gym.socialLinks?.instagram || gym.address || (gym.openingHours && Object.values(gym.openingHours).some(v => v)) || hasSpecialHours;
              const ga = gym.accentColor || DEFAULT_ACCENT;
              const closedLabel = nextOpen
                ? nextOpen.isToday
                  ? t('gymSelector.opensAtToday', { time: nextOpen.open })
                  : t('gymSelector.opensAt', { day: t('gymEditor.' + nextOpen.day + 'Full'), time: nextOpen.open })
                : t('gymSelector.closedNow');
              const statusLabel = openStatus === "closing" ? t('gymSelector.closingSoon') : openStatus ? t('gymSelector.openNow') : closedLabel;
              const statusBg = openStatus === "closing" ? "#f59e0b" : openStatus ? "#16a34a" : "#dc2626";
              // Resolve image URLs: same-origin Cloud Function endpoints for CDN caching & Cloudflare compatibility
              const logoSrc = gym.logoUrl || (gym.hasLogo ? `/manifest/${gym.slug}/icon.png` : null);
              const coverSrc = gym.hasCover ? `/manifest/${gym.slug}/cover.png` : null;
              const isRecent = recentGymIds.includes(gym.id);
              const isNonPublic = gym._nonPublic === true;
              return (
                // An article named by the gym: every card carries the same
                // „Részletek” toggle, and the card is what tells them apart.
                <div key={gym.id} className="sendly-gym-card" role="article" aria-label={gym.name} style={{
                  // CSS variable: per-card accent color the hover styles can
                  // blend with via color-mix(). Set inline so each card gets
                  // its own tint in the hover state.
                  ["--ga"]: ga,
                  background: th.card, borderRadius: 14,
                  border: isNonPublic ? "1px dashed #fcd34d" : "1px solid " + th.border,
                  overflow: "hidden",
                  opacity: isNonPublic ? 0.75 : 1,
                  position: "relative",
                }}>
                  {/* Cover or accent bar — navigates to gym */}
                  <div onClick={() => handleGymClick(gym)} style={{ cursor: "pointer" }}>
                    {coverSrc ? (
                      <div style={{ height: 100, background: ga + "22", position: "relative", overflow: "hidden" }}>
                        <img src={coverSrc} alt="" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        {openStatus !== null && (
                          <div style={{ position: "absolute", top: 8, right: 8, padding: "3px 10px", borderRadius: 999, fontSize: 12, fontWeight: 700,
                            background: statusBg, color: "#fff" }}>
                            {statusLabel}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ height: 8, background: ga, position: "relative" }}>
                        {openStatus !== null && (
                          <div style={{ position: "absolute", top: 12, right: 8, padding: "3px 10px", borderRadius: 999, fontSize: 12, fontWeight: 700,
                            background: statusBg, color: "#fff" }}>
                            {statusLabel}
                          </div>
                        )}
                      </div>
                    )}
                    <div style={{ padding: 16 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                        {logoSrc ? <img src={logoSrc} alt="" loading="lazy" style={{ width: 36, height: 36, borderRadius: 8, objectFit: "contain" }} /> :
                          <div style={{ width: 36, height: 36, borderRadius: 8, background: ga, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 800, fontSize: 16 }}>
                            {(gym.name || "S")[0]}
                          </div>}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: "block", fontSize: 16, fontWeight: 800, color: th.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{gym.name}</span>
                          {gym.city && <div style={{ fontSize: 12, color: th.textMuted }}>{gym.city}</div>}
                        </div>
                      </div>
                      {(() => {
                        // Collect stats-row items; disciplines, recent, non-public all
                        // share this row with solid-dot separators. Order is stable so
                        // separators land cleanly between visible items only.
                        const sepDot = (
                          <span style={{ display: "inline-block", width: 3, height: 3, borderRadius: "50%", background: th.textMuted, opacity: 0.5, flexShrink: 0 }} />
                        );
                        const items = [];
                        // Per-discipline route counts live in stats.activeRoutesBoulder /
                        // activeRoutesLead (written by the shard counters). When the gym
                        // offers both disciplines AND at least one of the per-discipline
                        // counters is non-zero, show them as separate stats
                        // ("Boulder 12", "Lead 5"). Single-discipline gyms — or gyms
                        // whose counters haven't been backfilled yet — fall back to the
                        // combined discipline label cluster.
                        const bCount = gym.stats?.activeRoutesBoulder | 0;
                        const lCount = gym.stats?.activeRoutesLead | 0;
                        const hasBoulder = gym.disciplines?.includes("boulder");
                        const hasLead = gym.disciplines?.includes("lead");
                        const multipleDisciplines = !!(hasBoulder && hasLead);
                        const splitAvailable = multipleDisciplines && (bCount + lCount > 0);
                        // Rating averages — "average of per-route averages" across
                        // all rated routes (active + archived) per discipline.
                        // null when no rated routes exist for that discipline.
                        const avgB = typeof gym.stats?.avgRatingBoulder === "number" ? gym.stats.avgRatingBoulder : null;
                        const avgL = typeof gym.stats?.avgRatingLead === "number" ? gym.stats.avgRatingLead : null;
                        const ratingPill = (val) => (
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 2, marginLeft: 6, color: th.textMuted, fontWeight: 500 }}>
                            <span style={{ color: "#f59e0b" }}>★</span>{val.toFixed(1)}
                          </span>
                        );
                        if (splitAvailable) {
                          if (bCount > 0) {
                            items.push(
                              <span key="routes-b" style={{ display: "inline-flex", alignItems: "center", gap: 4, color: th.text, fontWeight: 600 }}>
                                {t('discipline.boulder')} {bCount}
                                {avgB !== null && ratingPill(avgB)}
                              </span>
                            );
                          }
                          if (lCount > 0) {
                            items.push(
                              <span key="routes-l" style={{ display: "inline-flex", alignItems: "center", gap: 4, color: th.text, fontWeight: 600 }}>
                                {t('discipline.lead')} {lCount}
                                {avgL !== null && ratingPill(avgL)}
                              </span>
                            );
                          }
                        } else if (gym.stats?.activeRoutes > 0) {
                          // Single-discipline (or legacy-only) gym — surface
                          // whichever discipline has a rating. Boulder wins
                          // ties since legacy/missing-discipline routes count
                          // as boulder in the aggregate.
                          const combinedAvg = avgB !== null ? avgB : avgL;
                          items.push(
                            <span key="routes" style={{ display: "inline-flex", alignItems: "center", gap: 4, color: th.text, fontWeight: 600 }}>
                              {gym.stats.activeRoutes} {t('gymSelector.routes')}
                              {combinedAvg !== null && ratingPill(combinedAvg)}
                            </span>
                          );
                        }
                        if (gym.stats?.totalUsers > 0) {
                          items.push(<span key="users">{gym.stats.totalUsers} {t('gymSelector.members')}</span>);
                        }
                        if (isRecent) {
                          items.push(
                            <span key="recent" style={{ color: accent, fontWeight: 700 }}>
                              {t('gymSelector.recent')}
                            </span>
                          );
                        }
                        if (isNonPublic) {
                          items.push(
                            <span key="np" style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "#d97706", fontWeight: 700 }}>
                              <IconLock size={11} />
                              {t('gymSelector.notPublic')}
                            </span>
                          );
                        }
                        return (
                          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: th.textMuted, flexWrap: "wrap" }}>
                            {items.map((item, i) => (
                              <React.Fragment key={item.key}>
                                {i > 0 && sepDot}
                                {item}
                              </React.Fragment>
                            ))}
                            {gym._dist != null && (
                              <span style={{ marginLeft: "auto", fontWeight: 600, color: accent }}>
                                {formatDistance(gym._dist)}
                              </span>
                            )}
                          </div>
                        );
                      })()}
                      {gym.activeComp && (() => {
                        const comp = gym.activeComp;
                        return (
                          <div
                            className="sendly-gym-card-comp"
                            onClick={(e) => {
                              // Stop bubbling so the parent gym-click handler
                              // (which navigates without a tab hash) doesn't
                              // also fire and overwrite the destination.
                              e.stopPropagation();
                              // `?calFocus=list` mirrors the /calendar deep-link
                              // signal so the comp tab opens in List view —
                              // overrides the user's per-gym view preference.
                              handleGymClick(gym, "#comp", "?calFocus=list");
                            }}
                            style={{
                              display: "flex", alignItems: "center", gap: 6, marginTop: 8,
                              padding: "6px 10px", borderRadius: 10,
                              background: ga + "11", border: "1px solid " + ga + "33",
                              cursor: "pointer",
                            }}
                          >
                            <span style={{ fontSize: 14 }}>🏆</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 12, fontWeight: 700, color: ga, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{comp.name}</div>
                              {comp.startDate && (
                                <div style={{ fontSize: 12, color: th.textMuted }}>
                                  {formatDateSpan(comp.startDate, comp.endDate, lang)}
                                </div>
                              )}
                            </div>
                            <span className="sendly-gym-card-comp-arrow" style={{ fontSize: 14, color: ga, flexShrink: 0 }}>↗</span>
                          </div>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Expand toggle */}
                  {/* A native button that says whether the details are open
                      (it was a clickable div only a mouse could reach). */}
                  {hasDetails && (
                    <button type="button" aria-expanded={isExpanded} onClick={(e) => {
                      e.stopPropagation();
                      if (isExpanded) { setExpandedGym(null); setExpandedGymData(null); }
                      else {
                        setExpandedGym(gym.id);
                        // Lazy load full gym doc for grade range and other heavy data
                        getDoc(doc(db, "gyms", gym.id)).then(snap => {
                          if (snap.exists()) setExpandedGymData({ id: snap.id, ...snap.data() });
                        });
                      }
                    }}
                      style={{ width: "100%", padding: "8px 16px", background: "none", border: "none", borderTop: "1px solid " + th.border, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", gap: 6, fontSize: 12, color: th.textMuted, fontFamily: F }}>
                      <span>{isExpanded ? t('gymSelector.lessInfo') : t('gymSelector.moreInfo')}</span>
                      <span aria-hidden="true" style={{ transition: "transform 0.2s", transform: isExpanded ? "rotate(180deg)" : "none", fontSize: 12, color: th.textMuted }}>▼</span>
                    </button>
                  )}

                  {/* Expanded details (uses lazy-loaded full gym data for grade ranges) */}
                  {isExpanded && hasDetails && (() => {
                    const hasHours = (gym.openingHours && Object.values(gym.openingHours).some(v => v)) || hasSpecialHours;
                    // Use lazy-loaded full data for grade ranges
                    const fullGym = expandedGymData?.id === gym.id ? expandedGymData : null;
                    return (
                    <div style={{ padding: "0 16px 16px", display: "flex", flexDirection: "column", gap: 2 }}>
                      {gym.address && (
                        <a href={getNavigationUrl(gym.address + (gym.city ? ", " + gym.city : ""), gym.location)}
                          target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                          style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: ga, textDecoration: "none", fontWeight: 500, padding: "8px 0" }}>
                          <span style={{ fontSize: 16 }}>📍</span>
                          <span style={{ borderBottom: "1px dashed " + ga + "66", ...SELECTABLE }}>{gym.address}{gym.city ? ", " + gym.city : ""}</span>
                        </a>
                      )}
                      {gym.contactPhone && (
                        <a href={`tel:${gym.contactPhone}`} onClick={(e) => e.stopPropagation()}
                          style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: th.text, textDecoration: "none", padding: "8px 0" }}>
                          <span style={{ fontSize: 16 }}>📞</span>
                          <span style={SELECTABLE}>{gym.contactPhone}</span>
                        </a>
                      )}
                      {gym.contactEmail && (
                        <a href={`mailto:${gym.contactEmail}`} onClick={(e) => e.stopPropagation()}
                          style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: th.text, textDecoration: "none", padding: "8px 0" }}>
                          <span style={{ fontSize: 16 }}>✉️</span>
                          <span style={SELECTABLE}>{gym.contactEmail}</span>
                        </a>
                      )}
                      {gym.website && (
                        <a href={gym.website.startsWith("http") ? gym.website : "https://" + gym.website}
                          target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                          style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: th.text, textDecoration: "none", padding: "8px 0" }}>
                          <span style={{ fontSize: 16 }}>🌐</span>
                          <span style={SELECTABLE}>{gym.website}</span>
                        </a>
                      )}
                      {(gym.socialLinks?.facebook || gym.socialLinks?.instagram) && (
                        <div style={{ display: "flex", gap: 12, marginTop: 4 }}>
                          {gym.socialLinks?.facebook && (
                            <a href={gym.socialLinks.facebook.startsWith("http") ? gym.socialLinks.facebook : "https://facebook.com/" + gym.socialLinks.facebook}
                              target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                              style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#1877F2", textDecoration: "none", fontWeight: 600 }}>
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                              Facebook
                            </a>
                          )}
                          {gym.socialLinks?.instagram && (
                            <a href={gym.socialLinks.instagram.startsWith("http") ? gym.socialLinks.instagram : "https://instagram.com/" + gym.socialLinks.instagram}
                              target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                              style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#E4405F", textDecoration: "none", fontWeight: 600 }}>
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="#E4405F"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                              Instagram
                            </a>
                          )}
                        </div>
                      )}
                      {hasHours && (
                        <div style={{ marginTop: 4 }}>
                          <WeekSchedule openingHours={gym.openingHours} specialHours={gym.specialHours} timezone={gym.timezone} accent={ga} dense />
                        </div>
                      )}
                      {/* Grade range summary — loaded from full gym doc.
                          Mixed gyms store difficulties per discipline; render
                          one section per discipline with a small header.
                          Single-discipline gyms fall back to customDifficulty
                          and skip the per-discipline header. Shared with
                          GymInfoModal via <GradeRangeSummary />. */}
                      <GradeRangeSummary gym={fullGym} style={{ marginTop: 8 }} />
                    </div>
                    );
                  })()}
                </div>
              );
            })}

            {/* Show all button */}
            {hasMore && (
              <button onClick={() => setShowAll(true)} style={{
                padding: "14px 24px", borderRadius: 12, border: "2px solid " + th.border,
                background: "none", color: th.text, fontSize: 14, fontWeight: 700,
                cursor: "pointer", fontFamily: F, textAlign: "center",
              }}>
                {t('gymSelector.showAll', { count: sortedGyms.length })}
              </button>
            )}
          </div>
          </>
        )}
      </div>
      <div style={{ fontSize: 12, color: th.textMuted, textAlign: "center", padding: "16px 0 8px", letterSpacing: 1 }}>© 2026 Sendly</div>

      {/* Login modal overlay */}
      {showLogin && !authUser && (
        <ModalBackdrop onClose={closeLogin} zIndex={100} dim={0.5}>
          <LoginCard
 onClose={closeLogin}
            auth={{
              emailInput, setEmailInput, passwordInput, setPasswordInput,
              emailSent, setEmailSent, authError, setAuthError,
              needsEmailConfirm, handleConfirmEmail,
              isStandalone, isIOSStandalone, isPasswordAuthEnabled,
              handleLogin, handleAppleLogin, handleEmailLink,
              handlePasswordSignIn, handlePasswordSignUp,
            }}
            hint={(pendingGymSlug || pendingDest) && (
              <div style={{ fontSize: 12, color: th.textMuted, marginTop: 4, textAlign: "center" }}>
                {pendingGymSlug ? t('gymSelector.signInToEnter') : t('gymSelector.signInToContinue')}
              </div>
            )}
          />
        </ModalBackdrop>
      )}

    </div>
  );
}
